import type { RouteParams } from '../SportsbookWidget/types';

const SPORTSBOOK_URL_CHANGE_EVENT = 'sportsbook:urlchange';

const SPORTSBOOK_QUERY_KEYS = ['page', 'eventId', 'sportId', 'racingType', 'categoryIds', 'championshipIds', 'oddIds', 'catId', 'champId', 'couponType'] as const;

type SportsbookRouteQuery = {
  page: string;
  eventId?: number;
  sportId?: number;
  racingType?: number;
  categoryIds?: number[];
  championshipIds?: number[];
  oddIds?: number[];
  couponType?: number;
  shareCode?: string;
};

const toOptionalNumber = (value: string | null) => {
  if (!value) return undefined;

  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const parseCommaSeparatedNumbers = (value: string | null) => {
  if (!value) return undefined;

  const parsed = value
    .split(',')
    .map(Number)
    .filter(number => !Number.isNaN(number));

  return parsed.length > 0 ? parsed : undefined;
};

const parseShareCode = (value: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const parseSportsbookSearchParams = (searchParams: Pick<URLSearchParams, 'get'>): SportsbookRouteQuery => ({
  page: searchParams.get('page') || 'overview',
  eventId: toOptionalNumber(searchParams.get('eventId')),
  sportId: toOptionalNumber(searchParams.get('sportId')),
  racingType: toOptionalNumber(searchParams.get('racingType')),
  categoryIds: parseCommaSeparatedNumbers(searchParams.get('categoryIds')),
  championshipIds: parseCommaSeparatedNumbers(searchParams.get('championshipIds')),
  oddIds: parseCommaSeparatedNumbers(searchParams.get('oddIds')),
  couponType: toOptionalNumber(searchParams.get('couponType')),
  shareCode: parseShareCode(searchParams.get('shareCode')),
});

const toUrlParam = (value: string | number | number[] | undefined) => {
  if (value === undefined || value === '') return undefined;
  return Array.isArray(value) ? value.map(String) : String(value);
};

const buildUrlParamsFromRouteParams = (routeParams: RouteParams) => ({
  page: routeParams.page,
  eventId: toUrlParam(routeParams.eventId ?? routeParams.event?.id),
  sportId: toUrlParam(routeParams.sportId),
  racingType: toUrlParam(routeParams.racingType),
  categoryIds: toUrlParam(routeParams.categoryIds ?? (routeParams.catId !== undefined ? [routeParams.catId] : undefined)),
  championshipIds: toUrlParam(routeParams.championshipIds ?? (routeParams.champId !== undefined ? [routeParams.champId] : undefined)),
  couponType: toUrlParam(routeParams.couponType),
});

type SportsbookUrlParams = Record<string, string | string[] | undefined>;

const setSportsbookSearchParams = (searchParams: URLSearchParams, params: SportsbookUrlParams) => {
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    searchParams.set(key, Array.isArray(value) ? value.join(',') : value);
  });
};

const buildSportsbookHref = (pathname: string, params: SportsbookUrlParams) => {
  const searchParams = new URLSearchParams();
  setSportsbookSearchParams(searchParams, params);
  const search = searchParams.toString();
  return search ? `${pathname}?${search}` : pathname;
};

const updateUrlParams = (params: SportsbookUrlParams) => {
  const url = new URL(window.location.href);

  SPORTSBOOK_QUERY_KEYS.forEach(key => url.searchParams.delete(key));
  setSportsbookSearchParams(url.searchParams, params);

  if (url.toString() === window.location.href) return;

  window.history.pushState({}, '', url.toString());
  window.dispatchEvent(new CustomEvent(SPORTSBOOK_URL_CHANGE_EVENT));
};

export { buildSportsbookHref, buildUrlParamsFromRouteParams, parseSportsbookSearchParams, SPORTSBOOK_URL_CHANGE_EVENT, updateUrlParams };
export type { SportsbookRouteQuery };
